import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Download } from "lucide-react";
import { API } from "@/lib/api";

export default function Hero({ content, scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.1, 1.28]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const lines = [content.hero_line1, content.hero_line2, content.hero_line3].filter(Boolean);

  return (
    <section id="inicio" ref={ref} className="relative min-h-screen w-full overflow-hidden grain">
      {/* Parallax background */}
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 z-0">
        <img src={content.hero_background} alt="Comida peruana" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/45 to-black/80" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 min-h-screen flex flex-col justify-center pt-24 pb-16">
        {/* Dynamic logo */}
        <motion.img
          src={content.logo}
          alt={content.brand_name}
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="h-24 md:h-40 w-auto object-contain mb-6 md:mb-8 drop-shadow-[0_8px_24px_rgba(0,0,0,0.55)]"
          data-testid="hero-logo"
        />

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="text-white/80 uppercase tracking-[0.35em] text-xs md:text-sm mb-5 font-medium"
        >
          {content.tagline}
        </motion.p>

        <h1 className="font-display text-white font-black leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-8xl">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-[0.12em]">
              <motion.span
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="inline-block will-change-transform"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.7 }}
          className="mt-7 max-w-xl text-white/85 text-base md:text-lg leading-relaxed"
        >
          {content.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <a
            href={content.doordash_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-order-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#da251d] text-white font-semibold px-8 py-4 transition-transform duration-200 hover:bg-[#b91c1c] active:scale-95"
          >
            Ordenar en DoorDash
          </a>
          <a
            href={`${API}/menu/pdf`}
            data-testid="hero-download-menu-btn"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 bg-white/5 backdrop-blur-sm text-white font-semibold px-8 py-4 transition-colors duration-200 hover:bg-white/15"
          >
            <Download size={18} /> Descargar menú
          </a>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("historia")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/70 hover:text-white"
        aria-label="Bajar"
      >
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="block">
          <ArrowDown size={26} />
        </motion.span>
      </motion.button>
    </section>
  );
}
